import { memo } from 'react'
import deepEqual from 'deep-equal'
import { isEqual } from 'lodash'
import { THEMES } from 'constants.js'
import { dataTypes, formatterOptions, numberFormats } from '@adalo/constants'
import { currencies, DEFAULT_CURRENCY } from 'utils/currency'
import ToggleField from 'components/Shared/Forms/ToggleField'
import { IBinding } from 'utils/responsiveTypes'

import MenuControl from '../MenuControl'
import { Field } from './TableTypes'

type FormatterOptions = keyof typeof formatterOptions

interface TableMenuProps {
  field: Field
  updateBindingType: (option: IBinding['format']) => void
  updateBindingRequiredText: (value: boolean) => void
}

const TEXT_LENGTH_OPTIONS = [
  {
    value: 'singleLine',
    label: 'Single Line',
  },
  {
    value: 'multiLine',
    label: 'Multi Line',
  },
  {
    value: 'noMax',
    label: 'No Max',
  },
]

const COLUMN_WIDTH_OPTIONS = [
  {
    value: 'standard',
    label: 'Standard',
  },
  {
    value: 'wide',
    label: 'Wide',
  },
]

const IMAGE_RATIO_OPTIONS = [
  {
    value: 'landscape',
    label: 'Landscape',
  },
  {
    value: 'portrait',
    label: 'Portrait',
  },
  {
    value: 'square',
    label: 'Square',
  },
  {
    value: 'circle',
    label: 'Circle',
  },
]

const LOCATION_FORMAT_OPTIONS = [
  {
    value: 'fullAddress',
    label: 'Full Address',
  },
  {
    value: 'addressElements.address1',
    label: 'Street Address',
  },
  {
    value: 'addressElements.name',
    label: 'Name',
  },
  {
    value: 'addressElements.city',
    label: 'City',
  },
  {
    value: 'addressElements.state',
    label: 'State',
  },
  {
    value: 'addressElements.country',
    label: 'Country',
  },
  {
    value: 'addressElements.postalCode',
    label: 'Postal Code',
  },
  {
    value: 'coordinates.latitude',
    label: 'Latitude',
  },
  {
    value: 'coordinates.longitude',
    label: 'Longitude',
  },
]

const TableMenu = memo(
  ({
    field,
    updateBindingType,
    updateBindingRequiredText,
  }: TableMenuProps): JSX.Element => {
    const options = formatterOptions[field.type as FormatterOptions]

    const CURRENCY_TYPES = [
      numberFormats.CURRENCY as string,
      numberFormats.ABBREVIATED_CURRENCY as string,
    ]

    const bindingType = field.binding?.format?.type ?? ''
    const bindingCurrency =
      field.binding?.format?.[numberFormats.CURRENCY] ??
      field.binding?.format?.[numberFormats.ABBREVIATED_CURRENCY]
    const bindingTextLength = field.binding?.format?.textLength
    const bindingColumnWidth = field.binding?.format?.columnWidth
    const bindingImageRatio = field.binding?.format?.imageRatio
    const bindingRequired = field.binding?.format?.required
    const bindingRequiredText = field.binding?.format?.requiredText
    const bindingLocationFormat = field.binding?.format?.locationFormat

    switch (field.type) {
      case dataTypes.NUMBER:
        return (
          <>
            <MenuControl
              options={options}
              name="type"
              displayName="Number Format"
              value={bindingType ?? 'Default'}
              onChange={updateBindingType}
              comparator={deepEqual}
              placeholder="Default"
              menuTheme={THEMES.DATA}
            />
            {CURRENCY_TYPES.includes(bindingType) && (
              <MenuControl
                options={currencies}
                name="currency"
                displayName="Currency Type"
                value={bindingCurrency ?? DEFAULT_CURRENCY}
                onChange={updateBindingType}
                comparator={deepEqual}
                placeholder={DEFAULT_CURRENCY}
                menuTheme={THEMES.DATA}
              />
            )}
          </>
        )
      case dataTypes.TEXT:
        return (
          <>
            <MenuControl
              options={TEXT_LENGTH_OPTIONS}
              name="textLength"
              displayName="Text Length"
              value={bindingTextLength ?? 'Single Line'}
              onChange={updateBindingType}
              comparator={deepEqual}
              placeholder="Single Line"
              menuTheme={THEMES.DATA}
            />
            <MenuControl
              options={COLUMN_WIDTH_OPTIONS}
              name="columnWidth"
              displayName="Column Width"
              value={bindingColumnWidth ?? 'Standard'}
              onChange={updateBindingType}
              comparator={deepEqual}
              placeholder="Standard"
              menuTheme={THEMES.DATA}
            />
          </>
        )
      case dataTypes.IMAGE:
        return (
          <>
            <MenuControl
              options={IMAGE_RATIO_OPTIONS}
              name="imageRatio"
              displayName="Image Aspect Ratio"
              value={bindingImageRatio ?? 'Landscape'}
              onChange={updateBindingType}
              comparator={deepEqual}
              placeholder="Landscape"
              menuTheme={THEMES.DATA}
            />
            <MenuControl
              options={COLUMN_WIDTH_OPTIONS}
              name="columnWidth"
              displayName="Column Width"
              value={bindingColumnWidth ?? 'Standard'}
              onChange={updateBindingType}
              comparator={deepEqual}
              placeholder="Standard"
              menuTheme={THEMES.DATA}
            />
          </>
        )
      case dataTypes.DATE:
      case dataTypes.DATE_ONLY:
        return (
          <MenuControl
            options={options}
            name="type"
            displayName="Date Format"
            value={bindingType ?? 'Relative'}
            onChange={updateBindingType}
            comparator={deepEqual}
            placeholder="Relative"
            menuTheme={THEMES.DATA}
          />
        )
      case dataTypes.BOOLEAN:
        return (
          <ToggleField
            label="Required Error Text"
            value={bindingRequired}
            inputValue={bindingRequired ? bindingRequiredText : 'Not Required'}
            onChange={updateBindingRequiredText}
            boxed
            hideLabelOnDisabled
          />
        )
      case dataTypes.LOCATION:
        return (
          <MenuControl
            options={LOCATION_FORMAT_OPTIONS}
            name="locationFormat"
            displayName="Location Format"
            value={bindingLocationFormat ?? 'Full Address'}
            onChange={updateBindingType}
            comparator={deepEqual}
            placeholder="Full Address"
            menuTheme={THEMES.DATA}
          />
        )
      default:
        ;<></>
    }

    return <></>
  },
  isEqual
)

export default TableMenu
